import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { DAY_MS } from '../constants';
import type { Plant } from '../types';
import { formatDuration } from './plants';

const THIRSTY_RATIO = 0.75;
const ALMOST_CRITICAL_OFFSET_MS = 30 * 60 * 1000;
const ALMOST_CRITICAL_NOTIFICATION_OFFSET = 1;
const CRITICAL_NOTIFICATION_OFFSET = 2;
const MINIMUM_SCHEDULE_DELAY_MS = 1000;

export function supportsNativeNotifications() {
  return Capacitor.isNativePlatform();
}

function getNotificationBaseId(plantId: string) {
  const hash = Array.from(plantId).reduce((value, character) => {
    return ((value << 5) - value + character.charCodeAt(0)) | 0;
  }, 0);

  return Math.abs(hash % 300000000) * 3 + 1000;
}

function getScheduleDate(timestamp: number) {
  return new Date(Math.max(timestamp, Date.now() + MINIMUM_SCHEDULE_DELAY_MS));
}

function buildWateringReminderBody(plant: Plant, timing: string) {
  return `Your ${plant.name} in ${plant.chloromance_zone} ${timing}.`;
}

async function ensureAndroidChannel() {
  if (Capacitor.getPlatform() !== 'android') return;

  await LocalNotifications.createChannel({
    id: 'chloromance-reminders',
    name: 'WiltPower reminders',
    description: 'Plant watering reminders for thirsty and critical plants.',
    importance: 4,
    visibility: 1,
    sound: 'default',
  });
}

export async function requestReminderPermission() {
  if (!supportsNativeNotifications()) {
    if (!('Notification' in window)) return 'unsupported';
    if (Notification.permission === 'granted') return 'granted';
    return Notification.requestPermission();
  }

  const permissions = await LocalNotifications.checkPermissions();
  if (permissions.display === 'granted') return 'granted';

  const requestedPermissions = await LocalNotifications.requestPermissions();
  return requestedPermissions.display === 'granted' ? 'granted' : 'denied';
}

export async function cancelPlantReminders(plantId: string) {
  if (!supportsNativeNotifications()) return;

  const baseId = getNotificationBaseId(plantId);
  await LocalNotifications.cancel({
    notifications: [
      { id: baseId },
      { id: baseId + ALMOST_CRITICAL_NOTIFICATION_OFFSET },
      { id: baseId + CRITICAL_NOTIFICATION_OFFSET },
    ],
  });
}

export async function cancelPlantReminderList(plants: Plant[]) {
  if (!supportsNativeNotifications()) return;

  await Promise.all(plants.map((plant) => cancelPlantReminders(plant.id)));
}

export async function schedulePlantReminders(plant: Plant) {
  if (!supportsNativeNotifications()) return;

  await ensureAndroidChannel();
  await cancelPlantReminders(plant.id);

  const baseId = getNotificationBaseId(plant.id);
  const frequencyMs = Math.max(plant.watering_frequency_days, 1) * DAY_MS;
  const thirstyAt = plant.last_watered_timestamp + frequencyMs * THIRSTY_RATIO;
  const criticalAt = plant.last_watered_timestamp + frequencyMs;
  const almostCriticalAt = criticalAt - ALMOST_CRITICAL_OFFSET_MS;
  const now = Date.now();
  const hasFutureCritical = criticalAt > now;
  const thirstyLeadTime = formatDuration(Math.max(criticalAt - thirstyAt, MINIMUM_SCHEDULE_DELAY_MS));
  const criticalTiming = hasFutureCritical
    ? 'needs watering now'
    : `needed watering ${formatDuration(now - criticalAt)} ago`;

  await LocalNotifications.schedule({
    notifications: [
      ...(hasFutureCritical
        ? [{
            id: baseId,
            title: 'WiltPower draining',
            body: buildWateringReminderBody(plant, `will need watering in ${thirstyLeadTime}`),
            schedule: { at: getScheduleDate(thirstyAt) },
            channelId: 'chloromance-reminders',
            extra: { plantId: plant.id, state: 'Thirsty' },
          }]
        : []),
      ...(hasFutureCritical
        ? [{
            id: baseId + ALMOST_CRITICAL_NOTIFICATION_OFFSET,
            title: 'WiltPower almost critical',
            body: buildWateringReminderBody(plant, 'will need watering in 30 minutes'),
            schedule: { at: getScheduleDate(almostCriticalAt) },
            channelId: 'chloromance-reminders',
            extra: { plantId: plant.id, state: 'Almost Critical' },
          }]
        : []),
      {
        id: baseId + (hasFutureCritical ? CRITICAL_NOTIFICATION_OFFSET : 0),
        title: 'WiltPower critical',
        body: buildWateringReminderBody(plant, criticalTiming),
        schedule: { at: getScheduleDate(criticalAt) },
        channelId: 'chloromance-reminders',
        extra: { plantId: plant.id, state: 'Critical' },
      },
    ],
  });
}

export async function schedulePlantReminderList(plants: Plant[]) {
  if (!supportsNativeNotifications()) return;

  await Promise.all(plants.map(schedulePlantReminders));
}
